import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Typography,
    Radio,
    RadioGroup
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BaseDialog from '../common/BaseDialog';
import { appointmentTypeService, AppointmentType, CreateAppointmentTypeDto, UpdateAppointmentTypeDto } from '../../services/appointmentTypeService';
import { businessService, Business } from '../../services/businessService';
import { useAuth } from '../../contexts/AuthContext';
import { ChromePicker, ColorResult } from 'react-color';
import { extractApiErrorMessage } from '../../utils/apiUtils';

// Field order for consistent error message ordering (matches validator rule order)
const appointmentTypeFieldOrder = ['Name', 'Description', 'Color', 'TextColor', 'RequiresLocation'];

interface FormData {
    name: string;
    description: string;
    requiresLocation: boolean;
    color: string;
    textColor: string;
    icon?: string;
}

const initialFormData: FormData = {
    name: '',
    description: '',
    requiresLocation: false,
    color: '#000000',
    textColor: '#FFFFFF',
    icon: ''
};

interface AppointmentTypesTabProps {
    businessId: string;
}

const AppointmentTypesTab: React.FC<AppointmentTypesTabProps> = ({ businessId }) => {
    const { user } = useAuth();
    const [types, setTypes] = useState<AppointmentType[]>([]);
    // Use businessId from props directly instead of local state to avoid caching wrong values
    const selectedBusinessId = businessId;
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dialogError, setDialogError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [formData, setFormData] = useState<FormData>(initialFormData);
    const [selectedType, setSelectedType] = useState<AppointmentType | null>(null);
    const [deleteOption, setDeleteOption] = useState<'delete' | 'transfer'>('delete');
    const [transferToTypeId, setTransferToTypeId] = useState<string>('');
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showTextColorPicker, setShowTextColorPicker] = useState(false);
    const [businesses, setBusinesses] = useState<Business[]>([]);

    const loadTypes = useCallback(async (forceReload: boolean = false) => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await appointmentTypeService.getAppointmentTypes(businessId, forceReload);
            setTypes(data);
        } catch (err) {
            setError('Failed to load appointment types. Please try again later.');
            console.error('Error loading appointment types:', err);
        } finally {
            setIsLoading(false);
        }
    }, [businessId]);

    useEffect(() => {
        if (businessId) {
            loadTypes();
        }
    }, [businessId, loadTypes]);

    const loadBusinesses = async () => {
        if (!user?.isAdmin) return;
        
        try {
            const response = await businessService.getBusinesses();
            console.log('Businesses response:', response);
            if (Array.isArray(response)) {
                setBusinesses(response);
            } else {
                console.error('Businesses response is not an array:', response);
                setBusinesses([]);
            }
        } catch (err) {
            console.error('Error loading businesses:', err);
            setError('Failed to load businesses. Please try again later.');
            setBusinesses([]);
        }
    };

    useEffect(() => {
        if (user?.isAdmin) {
            loadBusinesses();
        }
    }, [user?.isAdmin]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            requiresLocation: e.target.checked
        }));
    };

    const handleCreate = async () => {
        try {
            setIsLoading(true);
            setDialogError(null);
            const newType: CreateAppointmentTypeDto = {
                name: formData.name,
                description: formData.description,
                requiresLocation: formData.requiresLocation,
                color: formData.color,
                textColor: formData.textColor,
                icon: formData.icon || undefined
            };
            await appointmentTypeService.createAppointmentType(newType, businessId);
            await loadTypes(true);
            setSuccess('Appointment type created successfully!');
            setIsCreateDialogOpen(false);
            setDialogError(null);
            setFormData(initialFormData);
        } catch (err: any) {
            console.error('Error creating appointment type:', err);
            setDialogError(extractApiErrorMessage(err, 'Failed to create appointment type', appointmentTypeFieldOrder));
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = async () => {
        if (!selectedType) return;

        try {
            setIsLoading(true);
            setDialogError(null);
            const updatedType: UpdateAppointmentTypeDto = {
                name: formData.name,
                description: formData.description,
                requiresLocation: formData.requiresLocation,
                color: formData.color,
                textColor: formData.textColor,
                icon: formData.icon || undefined
            };
            await appointmentTypeService.updateAppointmentType(selectedType.id, updatedType);
            await loadTypes(true);
            setSuccess('Appointment type updated successfully!');
            setIsEditDialogOpen(false);
            setDialogError(null);
            setSelectedType(null);
            setFormData(initialFormData);
        } catch (err: any) {
            console.error('Error updating appointment type:', err);
            setDialogError(extractApiErrorMessage(err, 'Failed to update appointment type', appointmentTypeFieldOrder));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedType) return;

        try {
            setIsLoading(true);
            setDialogError(null);
            await appointmentTypeService.deleteAppointmentType(selectedType.id, {
                deleteAppointments: deleteOption === 'delete',
                transferToTypeId: deleteOption === 'transfer' ? transferToTypeId : undefined
            });
            await loadTypes(true);
            setSuccess('Appointment type deleted successfully!');
            setIsDeleteDialogOpen(false);
            setDialogError(null);
            setSelectedType(null);
            setDeleteOption('delete');
            setTransferToTypeId('');
        } catch (err: any) {
            console.error('Error deleting appointment type:', err);
            setDialogError(extractApiErrorMessage(err, 'Failed to delete appointment type'));
        } finally {
            setIsLoading(false);
        }
    };

    const openCreateDialog = () => {
        setFormData(initialFormData);
        setDialogError(null);
        setIsCreateDialogOpen(true);
    };

    // Ensure color has # prefix for hex colors and trim whitespace
    const normalizeHexColor = (color: string | null | undefined, fallback: string): string => {
        if (!color) return fallback;
        const trimmed = color.trim();
        if (!trimmed) return fallback;
        // If it's a 6 or 3 character hex without #, add it
        if (/^[A-Fa-f0-9]{6}$/.test(trimmed) || /^[A-Fa-f0-9]{3}$/.test(trimmed)) {
            return `#${trimmed}`;
        }
        return trimmed;
    };

    const openEditDialog = (type: AppointmentType) => {
        setSelectedType(type);
        setFormData({
            name: type.name,
            description: type.description,
            requiresLocation: type.requiresLocation,
            color: normalizeHexColor(type.color, '#000000'),
            textColor: normalizeHexColor(type.textColor, '#FFFFFF'),
            icon: type.icon ?? ''
        });
        setDialogError(null);
        setIsEditDialogOpen(true);
    };

    const openDeleteDialog = (type: AppointmentType) => {
        setSelectedType(type);
        setDialogError(null);
        setIsDeleteDialogOpen(true);
    };

    const renderForm = () => (
        <>
            {dialogError && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{dialogError}</Alert>}
            <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                margin="normal"
            />
            <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                margin="normal"
                multiline
                rows={3}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={formData.requiresLocation}
                        onChange={handleSwitchChange}
                        name="requiresLocation"
                    />
                }
                label="Requires Location"
            />
            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Color</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            bgcolor: formData.color,
                            border: '1px solid #ccc',
                            cursor: 'pointer'
                        }}
                        onClick={() => setShowColorPicker(!showColorPicker)}
                    />
                    {showColorPicker && (
                        <Box sx={{ position: 'absolute', zIndex: 2 }}>
                            <Box
                                sx={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }}
                                onClick={() => setShowColorPicker(false)}
                            />
                            <ChromePicker
                                color={formData.color}
                                onChange={(color: ColorResult) => setFormData(prev => ({ ...prev, color: color.hex }))}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Text Color</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            bgcolor: formData.textColor,
                            border: '1px solid #ccc',
                            cursor: 'pointer'
                        }}
                        onClick={() => setShowTextColorPicker(!showTextColorPicker)}
                    />
                    {showTextColorPicker && (
                        <Box sx={{ position: 'absolute', zIndex: 2 }}>
                            <Box
                                sx={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }}
                                onClick={() => setShowTextColorPicker(false)}
                            />
                            <ChromePicker
                                color={formData.textColor}
                                onChange={(color: ColorResult) => setFormData(prev => ({ ...prev, textColor: color.hex }))}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
            <TextField
                fullWidth
                label="Icon (optional)"
                name="icon"
                value={formData.icon}
                onChange={handleInputChange}
                margin="normal"
                helperText="Enter an icon name from Material Icons"
            />
        </>
    );

    return (
        <Box sx={{ mt: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Button
                variant="contained"
                onClick={openCreateDialog}
                disabled={isLoading}
                sx={{ mb: 2 }}
            >
                Add Appointment Type
            </Button>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Location Required</TableCell>
                            <TableCell>Color</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {types.map((type) => (
                            <TableRow key={type.id}>
                                <TableCell>{type.name}</TableCell>
                                <TableCell>{type.description}</TableCell>
                                <TableCell>{type.requiresLocation ? 'Yes' : 'No'}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Box
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                bgcolor: type.color,
                                                border: '1px solid #ccc'
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                bgcolor: type.textColor,
                                                border: '1px solid #ccc'
                                            }}
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <IconButton onClick={() => openEditDialog(type)} disabled={isLoading}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => openDeleteDialog(type)} disabled={isLoading}>
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create Dialog */}
            <BaseDialog
                open={isCreateDialogOpen}
                onClose={() => {
                    setIsCreateDialogOpen(false);
                    setFormData(initialFormData);
                    setDialogError(null);
                }}
                title="Create Appointment Type"
                maxWidth="sm"
                fullWidth
                actions={
                    <>
                        <Button
                            onClick={() => {
                                setIsCreateDialogOpen(false);
                                setFormData(initialFormData);
                                setDialogError(null);
                            }}
                            variant="outlined"
                            color="primary">Cancel</Button>
                        <Button onClick={handleCreate} variant="contained" disabled={isLoading}>
                            Create
                        </Button>
                    </>
                }
            >
                {renderForm()}
            </BaseDialog>

            {/* Edit Dialog */}
            <BaseDialog
                open={isEditDialogOpen}
                onClose={() => {
                    setIsEditDialogOpen(false);
                    setFormData(initialFormData);
                    setSelectedType(null);
                    setDialogError(null);
                }}
                title="Edit Appointment Type"
                maxWidth="sm"
                fullWidth
                actions={
                    <>
                        <Button
                            onClick={() => {
                                setIsEditDialogOpen(false);
                                setFormData(initialFormData);
                                setSelectedType(null);
                                setDialogError(null);
                            }}
                            variant="outlined"
                            color="primary">Cancel</Button>
                        <Button onClick={handleEdit} variant="contained" disabled={isLoading}>
                            Save
                        </Button>
                    </>
                }
            >
                {renderForm()}
            </BaseDialog>

            {/* Delete Dialog */}
            <BaseDialog
                open={isDeleteDialogOpen}
                onClose={() => {
                    setIsDeleteDialogOpen(false);
                    setSelectedType(null);
                    setDialogError(null);
                    setDeleteOption('delete');
                    setTransferToTypeId('');
                }}
                title="Delete Appointment Type"
                fullWidth
                actions={
                    <>
                        <Button
                            onClick={() => {
                                setIsDeleteDialogOpen(false);
                                setSelectedType(null);
                                setDialogError(null);
                                setDeleteOption('delete');
                                setTransferToTypeId('');
                            }}
                            variant="outlined"
                            color="primary">Cancel</Button>
                        <Button
                            onClick={handleDelete}
                            variant="contained"
                            color="error"
                            disabled={isLoading || (deleteOption === 'transfer' && !transferToTypeId)}
                        >
                            Delete
                        </Button>
                    </>
                }
            >
                    {dialogError && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{dialogError}</Alert>}
                    <Typography>What would you like to do with existing appointments of this type?</Typography>
                    <RadioGroup
                        value={deleteOption}
                        onChange={(e) => setDeleteOption(e.target.value as 'delete' | 'transfer')}
                    >
                        <FormControlLabel
                            value="delete"
                            control={<Radio />}
                            label="Delete all appointments"
                        />
                        <FormControlLabel
                            value="transfer"
                            control={<Radio />}
                            label="Transfer appointments to another type"
                        />
                    </RadioGroup>
                    {deleteOption === 'transfer' && (
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>Transfer to Type</InputLabel>
                            <Select
                                value={transferToTypeId}
                                onChange={(e) => setTransferToTypeId(e.target.value)}
                                label="Transfer to Type"
                            >
                                {types
                                    .filter(t => t.id !== selectedType?.id)
                                    .map((type) => (
                                        <MenuItem key={type.id} value={type.id.toString()}>
                                            {type.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    )}
            </BaseDialog>
        </Box>
    );
};

export default AppointmentTypesTab; 